import type { ApolloClient } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import type { KeyboardLayout, ReducedMotionPreference } from '@words-happen/ui-components';
import {
  KeyboardLayout as ApiKeyboardLayout,
  MySettings,
  type MySettingsQuery,
  ReducedMotion,
  UpdateMySettings,
  type UpdateMySettingsMutation,
  type UpdateMySettingsMutationVariables,
} from '../../generated/graphql.api-app';
import { apolloClient } from '../apollo-client';

/** The settings saved to the player's account, as the game uses them. */
export interface AccountSettings {
  reducedMotion: ReducedMotionPreference;
  keyboardLayout: KeyboardLayout;
}

/** What a player has until they change a setting, and what guests see. */
export const DEFAULT_ACCOUNT_SETTINGS: Readonly<AccountSettings> = Object.freeze({
  reducedMotion: 'system',
  keyboardLayout: 'qwerty',
});

/** words-happen-gql's error code for a request with no valid session. */
export const UNAUTHENTICATED_ERROR_CODE = 'UAX-002';

const REDUCED_MOTION_FROM_API: Record<ReducedMotion, ReducedMotionPreference> = {
  [ReducedMotion.System]: 'system',
  [ReducedMotion.On]: 'on',
  [ReducedMotion.Off]: 'off',
};

const REDUCED_MOTION_TO_API: Record<ReducedMotionPreference, ReducedMotion> = {
  system: ReducedMotion.System,
  on: ReducedMotion.On,
  off: ReducedMotion.Off,
};

const KEYBOARD_LAYOUT_FROM_API: Record<ApiKeyboardLayout, KeyboardLayout> = {
  [ApiKeyboardLayout.Qwerty]: 'qwerty',
  [ApiKeyboardLayout.Alphabetical]: 'alphabetical',
};

const KEYBOARD_LAYOUT_TO_API: Record<KeyboardLayout, ApiKeyboardLayout> = {
  qwerty: ApiKeyboardLayout.Qwerty,
  alphabetical: ApiKeyboardLayout.Alphabetical,
};

type ApiSettings = MySettingsQuery['mySettings'];
type ApiSettingsInput = UpdateMySettingsMutationVariables['input'];

/** The settings from the API. A missing or unknown value (an older or newer API) reads as the default. */
export const fromApiSettings = (settings: ApiSettings): AccountSettings => ({
  reducedMotion: REDUCED_MOTION_FROM_API[settings.reducedMotion] ?? DEFAULT_ACCOUNT_SETTINGS.reducedMotion,
  keyboardLayout: KEYBOARD_LAYOUT_FROM_API[settings.keyboardLayout] ?? DEFAULT_ACCOUNT_SETTINGS.keyboardLayout,
});

/** The API input for a change: only the settings given. */
export const toApiSettingsInput = (patch: Partial<AccountSettings>): ApiSettingsInput => ({
  ...(patch.reducedMotion === undefined ? {} : { reducedMotion: REDUCED_MOTION_TO_API[patch.reducedMotion] }),
  ...(patch.keyboardLayout === undefined ? {} : { keyboardLayout: KEYBOARD_LAYOUT_TO_API[patch.keyboardLayout] }),
});

/** Whether a failed call failed because the session has expired or was never there. */
export const isUnauthenticatedError = (error: unknown): boolean =>
  CombinedGraphQLErrors.is(error) &&
  error.errors.some(({ extensions }) => extensions?.code === UNAUTHENTICATED_ERROR_CODE);

/** Reads and saves the signed-in player's account settings on words-happen-gql. Both reject when the call fails. */
export interface AccountSettingsApi {
  load: () => Promise<AccountSettings>;
  /** Saves only the given settings; resolves to all the player's saved settings. */
  save: (patch: Partial<AccountSettings>) => Promise<AccountSettings>;
}

export const createAccountSettingsApi = (client: ApolloClient = apolloClient): AccountSettingsApi => ({
  load: async () => {
    const { data } = await client.query<MySettingsQuery>({ query: MySettings, fetchPolicy: 'network-only' });
    if (!data) {
      throw new Error('mySettings returned no data');
    }
    return fromApiSettings(data.mySettings);
  },
  save: async (patch) => {
    const { data } = await client.mutate<UpdateMySettingsMutation, UpdateMySettingsMutationVariables>({
      mutation: UpdateMySettings,
      variables: { input: toApiSettingsInput(patch) },
    });
    if (!data) {
      throw new Error('updateMySettings returned no data');
    }
    return fromApiSettings(data.updateMySettings);
  },
});
